import { useState } from 'react';
import { ApiError } from '@dataroom/shared';
import {
  BLANK_TEMPLATE_FILENAME,
  BLANK_TEMPLATE_URL,
  DEFAULT_CHECKLIST_FILENAME,
  DEFAULT_CHECKLIST_XLSX_PATH,
  downloadFromApi,
  SAMPLE_CHECKLIST_FILENAME,
  SAMPLE_CHECKLIST_XLSX_PATH,
} from '@/lib/checklist';

/**
 * The starting points for an uploaded checklist: the blank Excel template (a
 * static file, always available), the SAMPLE layout — the standard checklist (an
 * API download, tenant admin only) — and the ClosingVault checklist rendered in
 * the same format (an API download, tenant admin on Plus or Professional only —
 * ADR-0044). Each optional link is hidden unless its flag is set.
 */
export function ChecklistTemplateLinks({
  showDefault,
  showSample = false,
}: {
  showDefault: boolean;
  showSample?: boolean;
}) {
  const [downloading, setDownloading] = useState<'default' | 'sample' | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function download(which: 'default' | 'sample') {
    setError(null);
    setDownloading(which);
    try {
      if (which === 'default') {
        await downloadFromApi(DEFAULT_CHECKLIST_XLSX_PATH, DEFAULT_CHECKLIST_FILENAME);
      } else {
        await downloadFromApi(SAMPLE_CHECKLIST_XLSX_PATH, SAMPLE_CHECKLIST_FILENAME);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Download failed.');
    } finally {
      setDownloading(null);
    }
  }

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
        {showSample ? (
          <button
            type="button"
            className="font-medium text-primary hover:underline disabled:opacity-50"
            onClick={() => void download('sample')}
            disabled={downloading !== null}
          >
            {downloading === 'sample' ? 'Preparing…' : 'Download the sample layout'}
          </button>
        ) : null}
        <a
          href={BLANK_TEMPLATE_URL}
          download={BLANK_TEMPLATE_FILENAME}
          className="font-medium text-primary hover:underline"
        >
          Download blank template
        </a>
        {showDefault ? (
          <button
            type="button"
            className="font-medium text-primary hover:underline disabled:opacity-50"
            onClick={() => void download('default')}
            disabled={downloading !== null}
          >
            {downloading === 'default' ? 'Preparing…' : 'Download the ClosingVault checklist as a starting point'}
          </button>
        ) : null}
      </div>
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}
